import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowForward from '@mui/icons-material/ArrowForward';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Button, Dialog, DialogProps, DialogTitle, Stack, Typography } from '@mui/material';

import { useLocalStorage } from 'usehooks-ts';

import { useGetCurrentUserQuery } from '@arrived/queries';

import { DataStateHandler } from '../../DataStateHandler';
import { PortfolioPrivacyContext, PortfolioPrivacyContextType } from './PortfolioPrivacyContext';

export type PortfolioPrivacyContextProviderProps = Omit<DialogProps, 'open' | 'onClose'>;

export const PortfolioPrivacyContextProvider = (props: PortfolioPrivacyContextProviderProps) => {
  const currentUserState = useGetCurrentUserQuery();

  return (
    <DataStateHandler queries={[currentUserState]}>
      {(currentUserState) => <PortfolioPrivacyContextProviderContent userId={currentUserState.data.id} {...props} />}
    </DataStateHandler>
  );
};

interface PortfolioPrivacyContextProviderContentProps extends PortfolioPrivacyContextProviderProps {
  userId: number;
}

const PortfolioPrivacyContextProviderContent = ({
  children,
  userId,
  sx,
  ...rest
}: PortfolioPrivacyContextProviderContentProps) => {
  const [isPrivacyModeEnabled, setIsPrivacyModeEnabled] = useLocalStorage<boolean | undefined>(
    `private-mode:${userId.toString()}`,
    undefined
  );
  // This ref stores whether or not the user has previously set private mode on. If they haven't then we want to
  // continue showing the confirmation dialog. Once they have enabled private mode once we don't need to show the
  // dialog again.
  const hasSetPrivacyMode = useRef(isPrivacyModeEnabled != null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const togglePrivacyMode = useCallback(() => {
    if (hasSetPrivacyMode.current) {
      setIsPrivacyModeEnabled((isPrivacyModeEnabled) => !isPrivacyModeEnabled);
    } else {
      setIsDialogOpen(true);
    }
  }, [setIsPrivacyModeEnabled]);

  const handleOnDialogClose = useCallback(() => setIsDialogOpen(false), []);

  const handleConfirmPrivacyMode = useCallback(() => {
    hasSetPrivacyMode.current = true;
    togglePrivacyMode();
    handleOnDialogClose();
  }, [handleOnDialogClose, togglePrivacyMode]);

  const contextValue = useMemo(
    (): PortfolioPrivacyContextType => ({ isPrivacyModeEnabled: Boolean(isPrivacyModeEnabled), togglePrivacyMode }),
    [isPrivacyModeEnabled, togglePrivacyMode]
  );

  return (
    <PortfolioPrivacyContext.Provider value={contextValue}>
      <Dialog
        onClose={handleOnDialogClose}
        open={isDialogOpen}
        sx={{
          '& .MuiPaper-root': {
            px: 4,
            py: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          ...sx,
        }}
        {...rest}
      >
        <DialogTitle>
          <FormattedMessage id="private-mode" defaultMessage="Private Mode" />
        </DialogTitle>
        <Stack direction="row" gap={1} alignItems="center">
          <Visibility sx={{ color: 'primary.main', fontSize: ({ fontSizes }) => fontSizes['5xl'] }} />
          <ArrowForward className="arrow" />
          <VisibilityOff sx={{ color: 'primary.main', fontSize: ({ fontSizes }) => fontSizes['5xl'] }} />
        </Stack>
        <Typography variant="h4.bold" sx={{ textAlign: 'center' }} mt={1}>
          <FormattedMessage id="privacy-mode-selected" defaultMessage="You've selected private mode" />
        </Typography>
        <Typography variant="body2" mt={2}>
          <FormattedMessage
            id="privacy-mode-description"
            defaultMessage="Click on the eye icon to hide or reveal your balances"
          />
        </Typography>
        <Stack width="100%" mt={4} alignItems="center" gap={2}>
          <Button onClick={handleConfirmPrivacyMode} sx={{ background: ({ gradients }) => gradients.sea.horizontal }}>
            <FormattedMessage id="private-mode" defaultMessage="Private Mode" />
          </Button>
          <Button onClick={handleOnDialogClose} sx={{ backgroundColor: 'gray.400' }}>
            <FormattedMessage id="not-now" defaultMessage="Not Now" />
          </Button>
        </Stack>
      </Dialog>
      {children}
    </PortfolioPrivacyContext.Provider>
  );
};
